import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Link from '@material-ui/core/Link'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Hidden from '@material-ui/core/Hidden'
import { useTheme } from '@material-ui/styles'
import { ToastContainer, toast } from 'react-toastify'
import CloseIcon from '@material-ui/icons/Close'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'

// contexts
import { useAppState } from '../../contexts/AppStateContext'
import { useClient } from '../../contexts/ClientContext'

// styles
import 'react-toastify/dist/ReactToastify.css'
import useStyles from './styles'

// components
import PageTitle from '../../components/PageTitle/PageTitle'
import Widget from '../../components/Widget/Widget'
import { Button } from '../../components/Wrappers'
import Notification from '../../components/Notification/Notification'

// utilities
import { useFormInput } from '../../customHooks'

export default function Dashboard () {
  const classes = useStyles()
  const theme = useTheme()
  const { appInitialized, globalError } = useAppState()
  const { client, createClient, updateWebhook, fetchClient } = useClient()

  const sendNotification = (componentProps, options) =>
    toast(<Notification {...componentProps} className={classes.notificationComponent} />, options)

  const [clientEnv, setClientEnv] = useState('sandbox')
  const [creationErrorText, setCreationErrorText] = useState('')
  const [apiSelectionErrorText, setApiSelectionErrorText] = useState('')
  const [pageProcessing, setPageProcessing] = useState(false)
  const [verifyCheckbox, setVerify] = useState(false)
  const [dispatchCheckbox, setDispatch] = useState(false)
  const [eventWebhook, setEventWebhook] = useState('')
  const [eventWebhookCurrent, setEventWebhookCurrent] = useState('')
  const [eventWebhookSecret, setEventWebhookSecret] = useState('')
  const [showEventWebhookSecret, setShowEventWebhookSecret] = useState(false)
  const [eventWebhookUpdateErrorText, setEventWebhookUpdateErrorText] = useState('')
  const [verificationWebhook, setVerificationWebhook] = useState('')
  const [verificationWebhookCurrent, setVerificationWebhookCurrent] = useState('')
  const [verificationWebhookSecret, setVerificationWebhookSecret] = useState('')
  const [showVerificationWebhookSecret, setShowVerificationWebhookSecret] = useState(false)
  const [verificationWebhookUpdateErrorText, setVerificationWebhookUpdateErrorText] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [showToken, setShowToken] = useState(false)

  const appName = useFormInput('')
  const appDesc = useFormInput('')

  const switchEnv = async (env) => {
    await fetchClient(env === 'production', false)
    setClientEnv(env)
  }

  const isEventWebhookDirty = () => eventWebhook !== eventWebhookCurrent
  const isVerificationWebhookDirty = () => verificationWebhook !== verificationWebhookCurrent

  function handleEventWebhookChange (e) {
    setEventWebhook(e.target.value)
  }

  function handleVerificationWebhookChange (e) {
    setVerificationWebhook(e.target.value)
  }

  useEffect(() => {
    setClientState()
  }, [client])

  const setClientState = () => {
    if (client) {
      const eventWebhooks = (client.webhooks && client.webhooks.filter(({ type }) => type === 'event')) || []
      const verificationWebhooks = (client.webhooks && client.webhooks.filter(({ type }) => type === 'verification')) || []
      const token = (client.tokens && client.tokens.length && client.tokens[0].access_token) || 'No token found!'
      setAccessToken(token)
      if (eventWebhooks.length) {
        setEventWebhook(eventWebhooks[0].url || '')
        setEventWebhookCurrent(eventWebhooks[0].url || '')
        setEventWebhookSecret(eventWebhooks[0].secret || '')
      } else {
        setEventWebhook('')
        setEventWebhookCurrent('')
        setEventWebhookSecret('')
      }
      if (verificationWebhooks.length) {
        setVerificationWebhook(verificationWebhooks[0].url || '')
        setVerificationWebhookCurrent(verificationWebhooks[0].url || '')
        setVerificationWebhookSecret(verificationWebhooks[0].secret || '')
      } else {
        setVerificationWebhook('')
        setVerificationWebhookCurrent('')
        setVerificationWebhookSecret('')
      }
      return
    }
    setAccessToken('')
    setEventWebhook('')
    setEventWebhookCurrent('')
    setEventWebhookSecret('')
    setShowEventWebhookSecret(false)
    setEventWebhookUpdateErrorText('')
    setVerificationWebhook('')
    setVerificationWebhookCurrent('')
    setVerificationWebhookSecret('')
    setShowVerificationWebhookSecret(false)
    setVerificationWebhookUpdateErrorText('')
    setApiSelectionErrorText('')
  }

  const createNewClient = async (e) => {
    if (clientEnv === 'production' && !verifyCheckbox && !dispatchCheckbox) {
      setApiSelectionErrorText('Access to at least one API needs to be requested')
      return
    }
    setPageProcessing(true)
    try {
      await createClient(appName.value, appDesc.value, clientEnv === 'production', verifyCheckbox, dispatchCheckbox)
      if (client) {
        const eventWebhooks = (client.webhooks && client.webhooks.filter(({ type }) => type === 'event')) || []
        const verificationWebhooks = (client.webhooks && client.webhooks.filter(({ type }) => type === 'verification')) || []
        const token = (client.tokens && client.tokens.length && client.tokens[0].access_token) || 'No token found!'
        setAccessToken(token)
        if (eventWebhooks.length) {
          setEventWebhook(eventWebhooks[0].url || '')
          setEventWebhookCurrent(eventWebhooks[0].url || '')
          setEventWebhookSecret(eventWebhooks[0].secret || '')
        }
        if (verificationWebhooks.length) {
          setVerificationWebhook(verificationWebhooks[0].url || '')
          setVerificationWebhookCurrent(verificationWebhooks[0].url || '')
          setVerificationWebhookSecret(verificationWebhooks[0].secret || '')
        }
      }
    } catch (error) {
      console.log(error)
      if (error.statusCode && error.body && error.body.details) {
        setCreationErrorText(error.body.details)
      } else {
        setCreationErrorText('An unknown error has occured.')
      }
    }
    setPageProcessing(false)
  }

  async function updateClient () {
    setPageProcessing(true)
    if (isEventWebhookDirty()) await updateEventWebhook()
    if (isVerificationWebhookDirty()) await updateVerificationWebhook()
    setPageProcessing(false)
  }

  async function updateEventWebhook () {
    try {
      if (!isValidURL(eventWebhook)) throw new Error('Invalid webhook URL')
      await updateWebhook('event', eventWebhook, clientEnv === 'production')
      sendNotification({
        type: 'report',
        message: 'Event webhook saved!',
        variant: 'contained',
        color: 'success'
      }, {
        type: 'success',
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
        progressClassName: classes.progress,
        className: classes.notification
      })
    } catch (error) {
      console.log(error)
      if (error.message === 'Invalid webhook URL') {
        setEventWebhookUpdateErrorText('Invalid webhook URL')
        return
      }
      if (error.statusCode && error.body && error.body.details) {
        if (error.statusCode === 400) setEventWebhookUpdateErrorText('Invalid webhook URL')
        else setEventWebhookUpdateErrorText(error.body.details)
      } else {
        setEventWebhookUpdateErrorText('An unknown error has occured.')
      }
    }
  }

  async function updateVerificationWebhook () {
    try {
      if (!isValidURL(verificationWebhook)) throw new Error('Invalid webhook URL')
      await updateWebhook('verification', verificationWebhook, clientEnv === 'production')
      sendNotification({
        type: 'report',
        message: 'Verification webhook saved!',
        variant: 'contained',
        color: 'success'
      }, {
        type: 'success',
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
        progressClassName: classes.progress,
        className: classes.notification
      })
    } catch (error) {
      console.log(error)
      if (error.message === 'Invalid webhook URL') {
        setVerificationWebhookUpdateErrorText('Invalid webhook URL')
        return
      }
      if (error.statusCode && error.body && error.body.details) {
        if (error.statusCode === 400) setVerificationWebhookUpdateErrorText('Invalid webhook URL')
        else setVerificationWebhookUpdateErrorText(error.body.details)
      } else {
        setVerificationWebhookUpdateErrorText('An unknown error has occured.')
      }
    }
  }

  const handleChange = (event) => {
    if (event.target.name === 'verify') {
      setVerify(event.target.checked)
    }
    if (event.target.name === 'dispatch') {
      setDispatch(event.target.checked)
    }
  }

  return (
  <>
    <ToastContainer
      className={classes.toastsContainer}
      closeButton={<CloseButton className={classes.notificationCloseButton} />}
      closeOnClick={false}
      progressClassName={classes.notificationProgress}
    />
    {globalError && <PageTitle title='Sorry!' />}

    {globalError &&
      <Typography variant='body1'>
        We were unable to fetch your app details. Please reload the page to retry.
      </Typography>
    }

    {!globalError && appInitialized && clientEnv !== 'production' && !client &&
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <PageTitle title='Create App' />
          <Widget disableWidgetMenu>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
                <Box>
                  <Typography variant='caption'>
                    Name *
                  </Typography>
                  <TextField
                    error={!!creationErrorText}
                    placeholder='My Hangout App'
                    variant='outlined'
                    fullWidth
                    inputProps={{ maxLength: '128' }}
                    helperText={creationErrorText}
                    onFocus={() => setCreationErrorText('')}
                    {...appName}
                  />
                </Box>
                <Box className={classes.mt30}>
                  <Typography variant='caption'>
                    Description *
                  </Typography>
                  <TextField
                    multiline
                    rows='4'
                    rowsMax='8'
                    placeholder='My Hangout App helps you discover new places to eat, drink, relax and play.'
                    variant='outlined'
                    fullWidth
                    inputProps={{ maxLength: '2048' }}
                    onFocus={() => setCreationErrorText('')}
                    {...appDesc}
                  />
                </Box>
                <Box className={classes.mt30}>
                  <Button
                    variant='contained'
                    size='large'
                    color='primary'
                    disabled={pageProcessing || !appName.value || !appDesc.value}
                    onClick={createNewClient}
                  >
                    Create
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Widget>
        </Grid>
      </Grid>
    }

      {!globalError && appInitialized && clientEnv === 'production' && !client &&
      <Grid container spacing={4}>
        <Hidden lgDown>
          <Grid item xl={1}></Grid>
        </Hidden>
        <Grid item xs={12} xl={10}>
          <PageTitle title='Create App' />
          <Box className={classes.envSelect}>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'sandbox' ? 'bold' : 'normal',
                color: clientEnv === 'sandbox' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('sandbox')}
            >
                      Sandbox
            </Typography>
            <Typography
              className={classes.envSelectDivider}
              variant='caption'
            >
                      |
            </Typography>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'production' ? 'bold' : 'normal',
                color: clientEnv === 'production' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('production')}
            >
                      Production
            </Typography>
          </Box>
          <Widget disableWidgetMenu>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
                <Box>
                  <Typography variant='caption'>
                                  Name *
                  </Typography>
                  <TextField
                    error={!!creationErrorText}
                    placeholder='My Hangout App'
                    variant='outlined'
                    fullWidth
                    inputProps={{ maxLength: '128' }}
                    helperText={creationErrorText}
                    onFocus={() => setCreationErrorText('')}
                    {...appName}
                  />
                </Box>
                <Box className={classes.mt30}>
                  <Typography
                    variant='caption'>
                    APIs that will be used in production *
                  </Typography>
                  <Grid container spacing={2} className={classes.formGroup}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dispatchCheckbox}
                          onChange={handleChange}
                          name="dispatch"
                          color="primary"
                        />
                      }
                      label="Dispatch API"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={verifyCheckbox}
                          onChange={handleChange}
                          name="verify"
                          color="primary"
                        />
                      }
                      label="Verify API"
                    />
                  </Grid>
                  { apiSelectionErrorText &&
                  <Typography
                    paragraph
                    variant='caption'
                    color='error'>
                    {apiSelectionErrorText}
                  </Typography>
                  }
                </Box>
                <Box className={classes.mt30}>
                  <Typography variant='caption'>
                                  Description *
                  </Typography>
                  <TextField
                    multiline
                    rows='4'
                    rowsMax='8'
                    placeholder='My Hangout App helps you discover new places to eat, drink, relax and play.'
                    variant='outlined'
                    fullWidth
                    inputProps={{ maxLength: '2048' }}
                    onFocus={() => setCreationErrorText('')}
                    {...appDesc}
                  />
                </Box>
                <Box className={classes.mt30}>
                  <Button
                    variant='contained'
                    size='large'
                    color='primary'
                    disabled={pageProcessing || !appName.value || !appDesc.value}
                    onClick={createNewClient}
                  >
                                  Create
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Widget>
        </Grid>
      </Grid>
      }

    {!globalError && appInitialized && client && clientEnv !== 'production' &&
      <Grid container spacing={4}>
        <Hidden lgDown>
          <Grid item xl={1}></Grid>
        </Hidden>
        <Grid item xs={12} xl={10}>
          <PageTitle title={client && client.display_name} />
          <Box className={classes.envSelect}>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'sandbox' ? 'bold' : 'normal',
                color: clientEnv === 'sandbox' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('sandbox')}
            >
              Sandbox
            </Typography>
            <Typography
              className={classes.envSelectDivider}
              variant='caption'
            >
              |
            </Typography>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'production' ? 'bold' : 'normal',
                color: clientEnv === 'production' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('production')}
            >
              Production
            </Typography>
          </Box>
          {clientEnv === 'sandbox' && <Widget disableWidgetMenu>
            <Box>
              <Typography variant='caption' component='div'>
                <strong>Server Token</strong>
              </Typography>
              {!showToken && <Button
                className={classes.mt10}
                size='small'
                variant='contained'
                disabled={pageProcessing}
                onClick={() => setShowToken(true)}
              >
                Show
              </Button>}
              {!!showToken && <Typography className={classes.mt10} variant='body2'>
                {accessToken}
              </Typography>
              }
            </Box>
            <Divider className={classes.divider} />
            <Box>
              <Typography variant='subtitle1'>
                Dispatch API
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption'>
                      <strong>Event Webhook URL </strong>
                      (Only needed when using <Link href='https://docs.noonlight.com/reference#dispatch-api-overview' target='_blank'>
                        Dispatch Service
                      </Link>.)
                    </Typography>
                    <TextField
                      error={!!eventWebhookUpdateErrorText}
                      variant='outlined'
                      fullWidth
                      value={eventWebhook}
                      onChange={handleEventWebhookChange}
                      disabled={pageProcessing}
                      helperText={eventWebhookUpdateErrorText}
                      inputProps={{ maxLength: '2048' }}
                      onFocus={() => setEventWebhookUpdateErrorText('')}
                    />
                  </Box>
                </Grid>
                <Hidden smDown><Grid item md={1}></Grid></Hidden>
                <Grid item xs={12} md={5}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption' component='div'>
                      <strong>Webhook Secret</strong>
                    </Typography>
                    {!eventWebhookSecret &&
                      <Typography variant='body2' className={classes.mt10}>
                        Not yet generated. Need to provide webhook.
                      </Typography>
                    }
                    {!!eventWebhookSecret && !showEventWebhookSecret &&
                    <Button
                      className={classes.mt10}
                      size='small'
                      variant='contained'
                      disabled={pageProcessing}
                      onClick={() => setShowEventWebhookSecret(true)}
                    >
                      Show
                    </Button>}
                    {!!eventWebhookSecret && !!showEventWebhookSecret &&
                    <Typography variant='body2' className={classes.mt10}>
                      {eventWebhookSecret}
                    </Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.mt30}>
              <Typography variant='subtitle1'>
                Verification API
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption'>
                      <strong>Verification Webhook URL </strong>
                      (Only needed when using <Link href='https://docs.noonlight.com/reference#verifications' target='_blank'>
                        Verification Service
                      </Link>.)
                    </Typography>
                    <TextField
                      error={!!verificationWebhookUpdateErrorText}
                      variant='outlined'
                      fullWidth
                      value={verificationWebhook}
                      onChange={handleVerificationWebhookChange}
                      disabled={pageProcessing}
                      helperText={verificationWebhookUpdateErrorText}
                      inputProps={{ maxLength: '2048' }}
                      onFocus={() => setVerificationWebhookUpdateErrorText('')}
                    />
                  </Box>
                </Grid>
                <Hidden smDown><Grid item md={1}></Grid></Hidden>
                <Grid item xs={12} md={5}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption' component='div'>
                      <strong>Webhook Secret</strong>
                    </Typography>
                    {!verificationWebhookSecret &&
                      <Typography variant='body2' className={classes.mt10}>
                        Not yet generated. Need to provide webhook.
                      </Typography>
                    }
                    {!!verificationWebhookSecret && !showVerificationWebhookSecret &&
                    <Button
                      className={classes.mt10}
                      size='small'
                      variant='contained'
                      disabled={pageProcessing}
                      onClick={() => setShowVerificationWebhookSecret(true)}
                    >
                      Show
                    </Button>}
                    {!!verificationWebhookSecret && !!showVerificationWebhookSecret &&
                    <Typography variant='body2' className={classes.mt10}>
                      {verificationWebhookSecret}
                    </Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.mt60}>
              <Button
                size='large'
                variant='contained'
                color='primary'
                disabled={pageProcessing || (!isEventWebhookDirty() && !isVerificationWebhookDirty())}
                onClick={updateClient}
              >
                Save
              </Button>
            </Box>
          </Widget>}
        </Grid>
      </Grid>
    }
      {!globalError && appInitialized && client && clientEnv === 'production' &&
      <Grid container spacing={4}>
        <Hidden lgDown>
          <Grid item xl={1}></Grid>
        </Hidden>
        <Grid item xs={12} xl={10}>
          <PageTitle title={client && client.display_name} />
          <Box className={classes.envSelect}>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'sandbox' ? 'bold' : 'normal',
                color: clientEnv === 'sandbox' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('sandbox')}
            >
                      Sandbox
            </Typography>
            <Typography
              className={classes.envSelectDivider}
              variant='caption'
            >
                      |
            </Typography>
            <Typography
              className={classes.envSelectOption}
              style={{
                fontWeight: clientEnv === 'production' ? 'bold' : 'normal',
                color: clientEnv === 'production' ? theme.palette.text.primary : theme.palette.text.hint
              }}
              variant='caption'
              onClick={() => switchEnv('production')}
            >
                      Production
            </Typography>
          </Box>
          <Widget disableWidgetMenu>
            <Box>
              <Typography variant='caption' component='div'>
                <strong>Server Token</strong>
              </Typography>
              {!showToken && <Button
                className={classes.mt10}
                size='small'
                variant='contained'
                disabled={pageProcessing}
                onClick={() => setShowToken(true)}
              >
                          Show
              </Button>}
              {!!showToken && <Typography className={classes.mt10} variant='body2'>
                {accessToken}
              </Typography>
              }
            </Box>
            <Divider className={classes.divider} />
            <Box>
              <Typography variant='subtitle1'>
                          Dispatch API
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption'>
                      <strong>Event Webhook URL </strong>
                                      (Only needed when using <Link href='https://docs.noonlight.com/reference#dispatch-api-overview' target='_blank'>
                                      Dispatch Service
                      </Link>.)
                    </Typography>
                    <TextField
                      error={!!eventWebhookUpdateErrorText}
                      variant='outlined'
                      fullWidth
                      value={eventWebhook}
                      onChange={handleEventWebhookChange}
                      disabled={pageProcessing}
                      helperText={eventWebhookUpdateErrorText}
                      inputProps={{ maxLength: '2048' }}
                      onFocus={() => setEventWebhookUpdateErrorText('')}
                    />
                  </Box>
                </Grid>
                <Hidden smDown><Grid item md={1}></Grid></Hidden>
                <Grid item xs={12} md={5}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption' component='div'>
                      <strong>Webhook Secret</strong>
                    </Typography>
                    {!eventWebhookSecret &&
                                  <Typography variant='body2' className={classes.mt10}>
                                      Not yet generated. Need to provide webhook.
                                  </Typography>
                    }
                    {!!eventWebhookSecret && !showEventWebhookSecret &&
                                  <Button
                                    className={classes.mt10}
                                    size='small'
                                    variant='contained'
                                    disabled={pageProcessing}
                                    onClick={() => setShowEventWebhookSecret(true)}
                                  >
                                      Show
                                  </Button>}
                    {!!eventWebhookSecret && !!showEventWebhookSecret &&
                                  <Typography variant='body2' className={classes.mt10}>
                                    {eventWebhookSecret}
                                  </Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.mt30}>
              <Typography variant='subtitle1'>
                          Verification API
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption'>
                      <strong>Verification Webhook URL </strong>
                                      (Only needed when using <Link href='https://docs.noonlight.com/reference#verifications' target='_blank'>
                                      Verification Service
                      </Link>.)
                    </Typography>
                    <TextField
                      error={!!verificationWebhookUpdateErrorText}
                      variant='outlined'
                      fullWidth
                      value={verificationWebhook}
                      onChange={handleVerificationWebhookChange}
                      disabled={pageProcessing}
                      helperText={verificationWebhookUpdateErrorText}
                      inputProps={{ maxLength: '2048' }}
                      onFocus={() => setVerificationWebhookUpdateErrorText('')}
                    />
                  </Box>
                </Grid>
                <Hidden smDown><Grid item md={1}></Grid></Hidden>
                <Grid item xs={12} md={5}>
                  <Box className={classes.mt10}>
                    <Typography variant='caption' component='div'>
                      <strong>Webhook Secret</strong>
                    </Typography>
                    {!verificationWebhookSecret &&
                                  <Typography variant='body2' className={classes.mt10}>
                                      Not yet generated. Need to provide webhook.
                                  </Typography>
                    }
                    {!!verificationWebhookSecret && !showVerificationWebhookSecret &&
                                  <Button
                                    className={classes.mt10}
                                    size='small'
                                    variant='contained'
                                    disabled={pageProcessing}
                                    onClick={() => setShowVerificationWebhookSecret(true)}
                                  >
                                      Show
                                  </Button>}
                    {!!verificationWebhookSecret && !!showVerificationWebhookSecret &&
                                  <Typography variant='body2' className={classes.mt10}>
                                    {verificationWebhookSecret}
                                  </Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.mt60}>
              <Button
                size='large'
                variant='contained'
                color='primary'
                disabled={pageProcessing || (!isEventWebhookDirty() && !isVerificationWebhookDirty())}
                onClick={updateClient}
              >
                          Save
              </Button>
            </Box>
          </Widget>
        </Grid>
      </Grid>
      }
  </>
  )
}

const isValidURL = (url) => RegExp(/^http.*$/).test(url)

function CloseButton ({ closeToast, className }) {
  return <CloseIcon className={className} onClick={closeToast} />
}
